import { AudioSystem } from '../assets/AudioSystem';
import { ScreenTransition } from './ScreenTransition';
import { getAllTranslations, type GameLanguage } from '../utils/Translations';
import { loadSettings } from '../types/SettingsTypes';

type PanelView = 'main' | 'play' | 'maps';
type UiClickSound = 'click' | 'confirm' | 'cancel' | 'none';
type UiSoundType = 'hover' | Exclude<UiClickSound, 'none'>;

interface MainMenuCallbacks {
  onSinglePlayer: () => void | Promise<void>;
  onBackToStartScreen?: () => void;
  onOpenMapEditor?: () => void;
  onOpenLoadMap?: () => void;
  onOpenSettings?: () => void;
  onOpenTowerInfo?: () => void;
  onOpenGameInfo?: () => void;
  onOpenAbout?: () => void;
  onOpenCredits?: () => void;
}

export class MainMenu {
  private root: HTMLDivElement;
  private mainPanel: HTMLDivElement;
  private playPanel: HTMLDivElement;
  private mapsPanel: HTMLDivElement;
  private messageArea: HTMLDivElement;
  private title: HTMLHeadingElement;
  private visible = false;
  private audioSystem?: AudioSystem | null;
  private transition?: ScreenTransition;
  private isStartingSinglePlayer = false;
  private currentLanguage: GameLanguage = 'en';
  private buttons: Map<string, HTMLButtonElement> = new Map();
  private fullVersionButton?: HTMLButtonElement;
  private fullVersionUnderscore?: HTMLSpanElement;
  private fullVersionPulseTime: number = 0;
  private animationFrameId?: number;

  constructor(
    private callbacks: MainMenuCallbacks,
    audioSystem?: AudioSystem | null,
    transition?: ScreenTransition,
  ) {
    this.root = document.createElement('div');
    this.root.style.position = 'fixed';
    this.root.style.inset = '0';
    this.root.style.display = 'none';
    this.root.style.justifyContent = 'center';
    this.root.style.alignItems = 'center';
    this.root.style.background = 'rgba(0, 0, 0, 0.9)';
    this.root.style.fontFamily = '"Courier New", monospace';
    this.root.style.zIndex = '9998';

    const panel = document.createElement('div');
    panel.style.background = '#111';
    panel.style.border = '2px solid #444';
    panel.style.padding = '40px';
    panel.style.minWidth = '360px';
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';
    panel.style.gap = '16px';
    panel.style.boxShadow = '0 0 40px rgba(0,0,0,0.6)';
    this.root.appendChild(panel);

    this.title = document.createElement('h2');
    this.title.style.color = '#fff';
    this.title.style.textAlign = 'center';
    this.title.style.margin = '0';
    panel.appendChild(this.title);

    this.mainPanel = this.createMainPanel();
    this.playPanel = this.createPlayPanel();
    this.mapsPanel = this.createMapsPanel();
    panel.appendChild(this.mainPanel);
    panel.appendChild(this.playPanel);
    panel.appendChild(this.mapsPanel);

    this.messageArea = document.createElement('div');
    this.messageArea.style.minHeight = '24px';
    this.messageArea.style.color = '#8de1ff';
    this.messageArea.style.fontSize = '13px';
    panel.appendChild(this.messageArea);

    document.body.appendChild(this.root);
    this.audioSystem = audioSystem;
    this.transition = transition;
    
    // Load language from settings
    const settings = loadSettings();
    this.currentLanguage = settings.language || 'en';
    this.updateTranslations();
    
    // Prevent right-click context menu
    this.root.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      // Go back or close
      if (this.playPanel.style.display !== 'none') {
        this.setPanel('main');
      } else if (this.mapsPanel.style.display !== 'none') {
        this.setPanel('play');
      } else {
        this.callbacks.onBackToStartScreen?.();
      }
      this.audioSystem?.playSound('ui_cancel');
    });
    
    // Prevent ESC from opening settings (only close/go back)
    document.addEventListener('keydown', (e) => {
      if (e.code === 'Escape' && this.visible) {
        e.preventDefault();
        if (this.playPanel.style.display !== 'none') {
          this.setPanel('main');
        } else if (this.mapsPanel.style.display !== 'none') {
          this.setPanel('play');
        } else {
          this.callbacks.onBackToStartScreen?.();
        }
        this.audioSystem?.playSound('ui_cancel');
      }
    });
    
    this.setPanel('main');
  }
  
  private updateTranslations(): void {
    const t = getAllTranslations(this.currentLanguage);
    
    // Update title
    if (this.title) {
      this.title.textContent = t.mainMenu;
    }
    
    // Update all buttons
    this.buttons.forEach((btn, key) => {
      const translationKey = key as keyof typeof t;
      if (t[translationKey]) {
        btn.textContent = t[translationKey];
      }
    });
  }
  
  public setLanguage(language: GameLanguage): void {
    this.currentLanguage = language;
    this.updateTranslations();
  }


  hide(): void {
    this.visible = false;
    this.root.style.display = 'none';
    this.stopFullVersionAnimation();
  }

  show(): void {
    this.visible = true;
    this.root.style.display = 'flex';
    this.setPanel('main');
    this.startFullVersionAnimation();
  }

  private startFullVersionAnimation(): void {
    if (!this.fullVersionButton || !this.fullVersionUnderscore) return;
    
    const animate = () => {
      if (!this.visible || !this.fullVersionButton || !this.fullVersionUnderscore) {
        this.stopFullVersionAnimation();
        return;
      }
      
      this.fullVersionPulseTime += 0.18;
      
      // Pulse underscore opacity (flashing effect)
      const underscoreOpacity = Math.abs(Math.sin(this.fullVersionPulseTime));
      this.fullVersionUnderscore.style.opacity = underscoreOpacity.toString();
      
      // Pulse glow effect
      const glowElement = document.getElementById('fullVersionGlow');
      if (glowElement) {
        const glowAlpha = 0.4 + Math.abs(Math.sin(this.fullVersionPulseTime * 1.2)) * 0.6;
        glowElement.style.opacity = glowAlpha.toString();
        
        // Pulse glow size
        const glowScale = 1.0 + Math.sin(this.fullVersionPulseTime * 1.5) * 0.15;
        glowElement.style.transform = `scale(${glowScale})`;
      }
      
      // Subtle button scale pulse
      const buttonScale = 1.0 + Math.sin(this.fullVersionPulseTime * 0.9) * 0.03;
      this.fullVersionButton.style.transform = `scale(${buttonScale})`;
      
      // Pulse border thickness
      const borderThickness = 2 + Math.sin(this.fullVersionPulseTime) * 1.5;
      if (!this.fullVersionButton.matches(':hover')) {
        this.fullVersionButton.style.borderWidth = `${Math.max(1, Math.min(4, borderThickness))}px`;
      }
      
      this.animationFrameId = requestAnimationFrame(animate);
    };
    
    this.animationFrameId = requestAnimationFrame(animate);
  }

  private stopFullVersionAnimation(): void {
    if (this.animationFrameId !== undefined) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  private createButton(label: string, handler: () => void | Promise<void>, options: { disabled?: boolean; sound?: UiClickSound, translationKey?: keyof ReturnType<typeof getAllTranslations> } = {}): HTMLButtonElement {
    const btn = document.createElement('button');
    const t = getAllTranslations(this.currentLanguage);
    btn.textContent = options.translationKey ? t[options.translationKey] : label;
    btn.style.padding = '10px 0';
    btn.style.fontSize = '16px';
    btn.style.fontFamily = '"Courier New", monospace';
    btn.style.background = '#252525';
    btn.style.color = '#fff';
    btn.style.border = '1px solid #555';
    btn.style.cursor = 'pointer';
    btn.style.transition = 'background 0.2s ease';
    btn.addEventListener('mouseenter', () => {
      if (!btn.disabled) {
        btn.style.background = '#3a3a3a';
        this.playUiSound('hover');
      }
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = '#252525';
    });
    btn.disabled = !!options.disabled;
    if (btn.disabled) {
      btn.style.opacity = '0.5';
      btn.style.cursor = 'not-allowed';
    } else {
      btn.addEventListener('click', () => {
        const soundType = options.sound ?? 'click';
        if (soundType !== 'none') {
          this.playUiSound(soundType as UiSoundType);
        }
        void Promise.resolve(handler());
      });
    }
    return btn;
  }

  private createMainPanel(): HTMLDivElement {
    const panel = document.createElement('div');
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';
    panel.style.gap = '12px';

    panel.appendChild(this.createButton('Play', () => {
      this.showMessage('');
      this.setPanel('play');
    }, { sound: 'confirm', translationKey: 'play' }));

    // Add FULL VERSION button for itch.io builds
    const isItchIoBuild = (typeof window !== 'undefined' && (window as any).__VITE_ITCH_IO_BUILD__ === true) ||
                          (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_ITCH_IO_BUILD === 'true');
    const isDevMode = typeof window !== 'undefined' && 
                      (window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       new URLSearchParams(window.location.search).get('showFullVersion') === 'true');
    
    if (isItchIoBuild || isDevMode) {
      // Create button container for pulsing effects
      const buttonContainer = document.createElement('div');
      buttonContainer.style.position = 'relative';
      buttonContainer.style.display = 'flex';
      buttonContainer.style.flexDirection = 'column';
      buttonContainer.style.alignItems = 'center';
      buttonContainer.style.gap = '4px';
      
      // Create glow effect behind button
      const glowElement = document.createElement('div');
      glowElement.style.position = 'absolute';
      glowElement.style.inset = '-5px';
      glowElement.style.background = 'rgba(139, 92, 246, 0.5)';
      glowElement.style.borderRadius = '4px';
      glowElement.style.zIndex = '-1';
      glowElement.style.filter = 'blur(8px)';
      glowElement.id = 'fullVersionGlow';
      buttonContainer.appendChild(glowElement);
      
      // Create the button
      this.fullVersionButton = this.createButton('FULL VERSION', () => {
        // Open full version link in new tab
        window.open('https://mazing-hunters.vercel.app/', '_blank');
        this.playUiSound('confirm');
      }, { sound: 'confirm' });
      
      // Style the button to stand out with pulsing effects
      this.fullVersionButton.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      this.fullVersionButton.style.border = '2px solid #8b5cf6';
      this.fullVersionButton.style.fontWeight = 'bold';
      this.fullVersionButton.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
      this.fullVersionButton.style.position = 'relative';
      this.fullVersionButton.style.transition = 'all 0.2s ease';
      this.fullVersionButton.id = 'fullVersionButton';
      
      this.fullVersionButton.addEventListener('mouseenter', () => {
        if (!this.fullVersionButton!.disabled) {
          this.fullVersionButton!.style.background = 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)';
          this.fullVersionButton!.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
          this.fullVersionButton!.style.border = '3px solid #9d6cf6';
        }
      });
      this.fullVersionButton.addEventListener('mouseleave', () => {
        this.fullVersionButton!.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        this.fullVersionButton!.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
        this.fullVersionButton!.style.border = '2px solid #8b5cf6';
      });
      
      buttonContainer.appendChild(this.fullVersionButton);
      
      // Create pulsing underscore
      this.fullVersionUnderscore = document.createElement('span');
      this.fullVersionUnderscore.textContent = '_';
      this.fullVersionUnderscore.style.color = '#ffffff';
      this.fullVersionUnderscore.style.fontSize = '18px';
      this.fullVersionUnderscore.style.fontWeight = 'bold';
      this.fullVersionUnderscore.style.fontFamily = '"Courier New", monospace';
      this.fullVersionUnderscore.style.position = 'absolute';
      this.fullVersionUnderscore.style.bottom = '-20px';
      this.fullVersionUnderscore.id = 'fullVersionUnderscore';
      buttonContainer.appendChild(this.fullVersionUnderscore);
      
      panel.appendChild(buttonContainer);
      
      // Start pulsing animation
      this.startFullVersionAnimation();
    }

    panel.appendChild(this.createButton('Settings', () => {
      this.showMessage('');
      this.hide();
      this.callbacks.onOpenSettings?.();
    }, { sound: 'click', translationKey: 'settings' }));

    panel.appendChild(this.createButton('Tower Info', () => {
      this.showMessage('');
      this.hide();
      this.callbacks.onOpenTowerInfo?.();
    }, { sound: 'click', translationKey: 'towerInfo' }));

    panel.appendChild(this.createButton('Game Info', () => {
      this.showMessage('');
      this.hide();
      this.callbacks.onOpenGameInfo?.();
    }, { sound: 'click', translationKey: 'gameInfo' }));

    panel.appendChild(this.createButton('About', () => {
      this.showMessage('');
      this.hide();
      this.callbacks.onOpenAbout?.();
    }, { sound: 'click', translationKey: 'about' }));

    panel.appendChild(this.createButton('Credits', () => {
      this.showMessage('');
      this.hide();
      this.callbacks.onOpenCredits?.();
    }, { sound: 'click', translationKey: 'credits' }));

    panel.appendChild(this.createButton('Back to Start Screen', () => {
      this.showMessage('');
      this.hide();
      this.callbacks.onBackToStartScreen?.();
    }, { sound: 'cancel', translationKey: 'backToStartScreen' }));

    return panel;
  }

  private createPlayPanel(): HTMLDivElement {
    const panel = document.createElement('div');
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';
    panel.style.gap = '12px';

    const title = document.createElement('h3');
    title.textContent = 'Play';
    title.style.color = '#fff';
    title.style.margin = '0 0 10px 0';
    panel.appendChild(title);

    panel.appendChild(this.createButton('Single Player (local)', () => this.handleSinglePlayerStart(), { sound: 'none', translationKey: 'singlePlayer' }));

    panel.appendChild(this.createButton('4-Player Co-op (placeholder)', () => {
      this.showMessage('Co-op mode is under construction.');
    }, { disabled: true, translationKey: 'coOp' }));

    panel.appendChild(this.createButton('PVP 8-Player Survival (placeholder)', () => {
      this.showMessage('PVP mode is under construction.');
    }, { disabled: true, translationKey: 'pvp' }));

    panel.appendChild(this.createButton('Maps', () => {
      this.showMessage('');
      this.setPanel('maps');
    }, { sound: 'confirm', translationKey: 'maps' }));

    const backButton = this.createButton('Back', () => {
      this.showMessage('');
      this.setPanel('main');
    }, { sound: 'cancel', translationKey: 'back' });
    backButton.style.marginTop = '8px';
    panel.appendChild(backButton);

    return panel;
  }

  private createMapsPanel(): HTMLDivElement {
    const panel = document.createElement('div');
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';
    panel.style.gap = '12px';

    const title = document.createElement('h3');
    title.textContent = 'Maps';
    title.style.color = '#fff';
    title.style.margin = '0 0 10px 0';
    panel.appendChild(title);

    panel.appendChild(this.createButton('Load Map', () => {
      this.hide();
      this.callbacks.onOpenLoadMap?.();
    }, { sound: 'confirm', translationKey: 'loadMap' }));

    panel.appendChild(this.createButton('Editor', () => {
      this.callbacks.onOpenMapEditor?.();
    }, { sound: 'confirm', translationKey: 'editor' }));

    const backButton = this.createButton('Back', () => {
      this.showMessage('');
      this.setPanel('play');
    }, { sound: 'cancel', translationKey: 'back' });
    backButton.style.marginTop = '8px';
    panel.appendChild(backButton);

    return panel;
  }

  private setPanel(panel: PanelView): void {
    this.mainPanel.style.display = panel === 'main' ? 'flex' : 'none';
    this.playPanel.style.display = panel === 'play' ? 'flex' : 'none';
    this.mapsPanel.style.display = panel === 'maps' ? 'flex' : 'none';
  }

  private showMessage(message: string): void {
    this.messageArea.textContent = message;
  }

  private playUiSound(type: UiSoundType): void {
    if (!this.audioSystem) return;
    const soundMap: Record<UiSoundType, string> = {
      hover: 'ui_hover',
      click: 'button_click',
      confirm: 'ui_confirm',
      cancel: 'ui_cancel',
    };
    this.audioSystem.playSound(soundMap[type]);
  }

  private playSinglePlayerSound(): void {
    this.audioSystem?.playSound('menu_single_player');
  }

  private async handleSinglePlayerStart(): Promise<void> {
    if (this.isStartingSinglePlayer) return;
    this.isStartingSinglePlayer = true;
    this.playSinglePlayerSound();

    const startSession = async () => {
      this.hide();
      await Promise.resolve(this.callbacks.onSinglePlayer());
    };

    if (this.transition) {
      await this.transition.fadeOutIn(startSession);
    } else {
      await startSession();
    }

    this.isStartingSinglePlayer = false;
  }
}

